/**
 * Equipment & Tool Rental Cost Estimator — data, types, and merge logic.
 * Pure module: no React, no server imports.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EquipmentCategory {
	slug: string
	name: string
	seoHeading: string
	icon: string
	description: string
	dbCategorySlugs: string[]
}

export interface EquipmentType {
	slug: string
	name: string
	categorySlug: string
	description: string
	keywords: string[]
	dbSubcategorySlugs: string[]
	relatedSlugs?: string[]
}

export interface StaticVendorPrice {
	equipmentSlug: string
	dailyCents: number | null
	weeklyCents: number | null
	monthlyCents: number | null
	depositCents: number | null
	notes: string
	isIllustrative: true
}

export interface MergedVendorPrice {
	vendorName: string
	vendorSlug: string | null
	dailyCents: number | null
	weeklyCents: number | null
	monthlyCents: number | null
	depositCents: number | null
	notes: string
	isIllustrative: boolean
}

export interface ProjectGuide {
	slug: string
	title: string
	icon: string
	description: string
	equipmentSlugs: string[]
	tips: string[]
}

// ---------------------------------------------------------------------------
// Categories (8)
// ---------------------------------------------------------------------------

export const EQUIPMENT_CATEGORIES: EquipmentCategory[] = [
	{
		slug: 'lawn-garden',
		name: 'Lawn & Garden',
		seoHeading: 'Lawn & Garden Equipment Rental Costs',
		icon: 'trees',
		description: 'Aerators, tillers, chainsaws, stump grinders & more',
		dbCategorySlugs: ['lawn-and-garden-rental'],
	},
	{
		slug: 'concrete-masonry',
		name: 'Concrete & Masonry',
		seoHeading: 'Concrete & Masonry Tool Rental Prices',
		icon: 'layers',
		description: 'Concrete saws, mixers, plate compactors & rammers',
		dbCategorySlugs: ['tool-rental', 'heavy-equipment-rental'],
	},
	{
		slug: 'demolition-excavation',
		name: 'Demolition & Excavation',
		seoHeading: 'Demolition & Excavation Equipment Rates',
		icon: 'axe',
		description: 'Mini excavators, jackhammers, skid steers & trenchers',
		dbCategorySlugs: ['heavy-equipment-rental', 'tool-rental'],
	},
	{
		slug: 'lifts-access',
		name: 'Lifts & Access',
		seoHeading: 'Lift & Aerial Equipment Rental Costs',
		icon: 'arrow-up',
		description: 'Scissor lifts, boom lifts, scaffolding & forklifts',
		dbCategorySlugs: ['heavy-equipment-rental'],
	},
	{
		slug: 'floor-care',
		name: 'Floor Care',
		seoHeading: 'Floor Care Equipment Rental Prices',
		icon: 'home',
		description: 'Floor sanders, buffers, carpet cleaners & tile saws',
		dbCategorySlugs: ['tool-rental'],
	},
	{
		slug: 'power-tools',
		name: 'Power Tools',
		seoHeading: 'Power Tool Rental Costs',
		icon: 'drill',
		description: 'Pressure washers, nail guns, drills, rotary hammers & more',
		dbCategorySlugs: ['tool-rental'],
	},
	{
		slug: 'generators-power',
		name: 'Generators & Power',
		seoHeading: 'Generator & Power Equipment Rental Rates',
		icon: 'sparkles',
		description: 'Generators, air compressors & welders',
		dbCategorySlugs: ['heavy-equipment-rental', 'tool-rental'],
	},
	{
		slug: 'material-handling',
		name: 'Material Handling',
		seoHeading: 'Material Handling Equipment Rental Costs',
		icon: 'forklift',
		description: 'Forklifts, pallet jacks, hand trucks & dollies',
		dbCategorySlugs: ['heavy-equipment-rental'],
	},
]

// ---------------------------------------------------------------------------
// Equipment Types (~35)
// ---------------------------------------------------------------------------

export const EQUIPMENT_TYPES: EquipmentType[] = [
	// Lawn & Garden
	{
		slug: 'aerator',
		name: 'Lawn Aerator',
		categorySlug: 'lawn-garden',
		description:
			'Core aerators improve soil compaction and promote healthier lawns.',
		keywords: ['aerator rental', 'lawn aerator rental dallas'],
		dbSubcategorySlugs: ['aerator-rental'],
		relatedSlugs: ['tiller', 'lawn-mower'],
	},
	{
		slug: 'tiller',
		name: 'Tiller / Rototiller',
		categorySlug: 'lawn-garden',
		description:
			'Break up hard soil for gardens, flower beds, and landscaping.',
		keywords: ['tiller rental', 'rototiller rental dfw'],
		dbSubcategorySlugs: ['tiller-rental'],
		relatedSlugs: ['aerator', 'sod-cutter'],
	},
	{
		slug: 'chainsaw',
		name: 'Chainsaw',
		categorySlug: 'lawn-garden',
		description: 'Gas and electric chainsaws for tree trimming and removal.',
		keywords: ['chainsaw rental', 'chainsaw rental near me'],
		dbSubcategorySlugs: ['chainsaw-rental'],
		relatedSlugs: ['stump-grinder', 'wood-chipper'],
	},
	{
		slug: 'stump-grinder',
		name: 'Stump Grinder',
		categorySlug: 'lawn-garden',
		description:
			'Remove tree stumps quickly with a self-propelled stump grinder.',
		keywords: ['stump grinder rental', 'stump grinder rental dallas'],
		dbSubcategorySlugs: ['stump-grinder-rental'],
		relatedSlugs: ['chainsaw', 'mini-excavator'],
	},
	{
		slug: 'wood-chipper',
		name: 'Wood Chipper',
		categorySlug: 'lawn-garden',
		description: 'Chip branches and yard debris into mulch.',
		keywords: ['wood chipper rental', 'chipper rental dfw'],
		dbSubcategorySlugs: ['wood-chipper-rental'],
		relatedSlugs: ['chainsaw', 'brush-cutter'],
	},
	{
		slug: 'sod-cutter',
		name: 'Sod Cutter',
		categorySlug: 'lawn-garden',
		description: 'Strip sod for re-landscaping or patio prep.',
		keywords: ['sod cutter rental', 'sod cutter rental dallas'],
		dbSubcategorySlugs: ['sod-cutter-rental'],
		relatedSlugs: ['tiller', 'plate-compactor'],
	},
	{
		slug: 'log-splitter',
		name: 'Log Splitter',
		categorySlug: 'lawn-garden',
		description: 'Hydraulic log splitters for firewood processing.',
		keywords: ['log splitter rental', 'log splitter rental near me'],
		dbSubcategorySlugs: ['log-splitter-rental'],
		relatedSlugs: ['chainsaw', 'wood-chipper'],
	},
	{
		slug: 'post-hole-digger',
		name: 'Post Hole Digger / Auger',
		categorySlug: 'lawn-garden',
		description:
			'Auger-style diggers for fence posts, deck footings, and planting holes.',
		keywords: [
			'post hole digger rental',
			'auger rental dallas',
			'auger rental',
		],
		dbSubcategorySlugs: ['post-hole-digger-rental'],
		relatedSlugs: ['tiller', 'sod-cutter'],
	},
	{
		slug: 'lawn-mower',
		name: 'Lawn Mower',
		categorySlug: 'lawn-garden',
		description:
			'Walk-behind and riding mowers for residential and commercial lawns.',
		keywords: ['lawn mower rental', 'lawn mower rental dallas'],
		dbSubcategorySlugs: ['lawn-mower-rental'],
		relatedSlugs: ['aerator', 'hedge-trimmer'],
	},
	{
		slug: 'hedge-trimmer',
		name: 'Hedge Trimmer',
		categorySlug: 'lawn-garden',
		description:
			'Gas and electric hedge trimmers for shaping shrubs and hedges.',
		keywords: ['hedge trimmer rental', 'hedge trimmer rental near me'],
		dbSubcategorySlugs: ['hedge-trimmer-rental'],
		relatedSlugs: ['chainsaw', 'lawn-mower'],
	},
	{
		slug: 'brush-cutter',
		name: 'Brush Cutter',
		categorySlug: 'lawn-garden',
		description:
			'Heavy-duty string trimmers and brush cutters for overgrown areas.',
		keywords: ['brush cutter rental', 'brush cutter rental dfw'],
		dbSubcategorySlugs: ['brush-cutter-rental'],
		relatedSlugs: ['chainsaw', 'wood-chipper'],
	},

	// Concrete & Masonry
	{
		slug: 'concrete-saw',
		name: 'Concrete Saw',
		categorySlug: 'concrete-masonry',
		description:
			'Walk-behind and handheld saws for cutting concrete, asphalt, and masonry.',
		keywords: ['concrete saw rental', 'concrete saw rental dallas'],
		dbSubcategorySlugs: ['concrete-saw-rental'],
		relatedSlugs: ['plate-compactor', 'jackhammer', 'concrete-mixer'],
	},
	{
		slug: 'plate-compactor',
		name: 'Plate Compactor',
		categorySlug: 'concrete-masonry',
		description:
			'Compact gravel, sand, and soil for walkways, patios, and foundations.',
		keywords: ['plate compactor rental', 'compactor rental dfw'],
		dbSubcategorySlugs: ['plate-compactor-rental'],
		relatedSlugs: ['concrete-saw', 'sod-cutter', 'concrete-mixer'],
	},
	{
		slug: 'tile-saw',
		name: 'Tile Saw',
		categorySlug: 'concrete-masonry',
		description:
			'Wet tile saws for precise cuts on ceramic, porcelain, and stone.',
		keywords: ['tile saw rental', 'tile saw rental near me'],
		dbSubcategorySlugs: ['tile-saw-rental'],
		relatedSlugs: ['concrete-saw', 'floor-sander'],
	},
	{
		slug: 'concrete-mixer',
		name: 'Concrete Mixer',
		categorySlug: 'concrete-masonry',
		description:
			'Portable and towable concrete mixers for footings, posts, and small pours.',
		keywords: ['concrete mixer rental', 'concrete mixer rental cost'],
		dbSubcategorySlugs: ['concrete-mixer-rental'],
		relatedSlugs: ['concrete-saw', 'plate-compactor', 'concrete-vibrator'],
	},
	{
		slug: 'concrete-vibrator',
		name: 'Concrete Vibrator',
		categorySlug: 'concrete-masonry',
		description:
			'Internal vibrators for removing air pockets and consolidating wet concrete.',
		keywords: ['concrete vibrator rental', 'concrete vibrator rental near me'],
		dbSubcategorySlugs: ['concrete-vibrator-rental'],
		relatedSlugs: ['concrete-mixer', 'plate-compactor'],
	},
	{
		slug: 'mortar-mixer',
		name: 'Mortar Mixer',
		categorySlug: 'concrete-masonry',
		description: 'Paddle-style mixers for mortar, stucco, and grout.',
		keywords: ['mortar mixer rental', 'mortar mixer rental near me'],
		dbSubcategorySlugs: ['mortar-mixer-rental'],
		relatedSlugs: ['concrete-mixer', 'tile-saw'],
	},
	{
		slug: 'rebar-cutter',
		name: 'Rebar Cutter / Bender',
		categorySlug: 'concrete-masonry',
		description:
			'Electric and hydraulic rebar cutters and benders for concrete reinforcement.',
		keywords: ['rebar cutter rental', 'rebar bender rental'],
		dbSubcategorySlugs: ['rebar-cutter-rental'],
		relatedSlugs: ['concrete-saw', 'concrete-mixer'],
	},

	// Demolition & Excavation
	{
		slug: 'mini-excavator',
		name: 'Mini Excavator',
		categorySlug: 'demolition-excavation',
		description:
			'Compact excavators for digging, trenching, and light demolition.',
		keywords: [
			'mini excavator rental',
			'mini excavator rental dallas',
			'excavator rental dfw',
		],
		dbSubcategorySlugs: ['mini-excavator-rental'],
		relatedSlugs: ['skid-steer', 'trencher', 'jackhammer'],
	},
	{
		slug: 'jackhammer',
		name: 'Jackhammer',
		categorySlug: 'demolition-excavation',
		description:
			'Electric and pneumatic jackhammers for breaking concrete and asphalt.',
		keywords: ['jackhammer rental', 'jackhammer rental dallas'],
		dbSubcategorySlugs: ['jackhammer-rental'],
		relatedSlugs: ['mini-excavator', 'concrete-saw', 'skid-steer'],
	},
	{
		slug: 'skid-steer',
		name: 'Skid Steer',
		categorySlug: 'demolition-excavation',
		description:
			'Versatile loaders for grading, excavation, and material handling.',
		keywords: [
			'skid steer rental',
			'bobcat rental dallas',
			'bobcat rental cost per day',
		],
		dbSubcategorySlugs: ['skid-steer-rental'],
		relatedSlugs: ['mini-excavator', 'trencher', 'plate-compactor'],
	},
	{
		slug: 'trencher',
		name: 'Trencher',
		categorySlug: 'demolition-excavation',
		description:
			'Walk-behind and ride-on trenchers for utility and irrigation lines.',
		keywords: [
			'trencher rental',
			'trencher rental dfw',
			'trencher rental cost',
		],
		dbSubcategorySlugs: ['trencher-rental'],
		relatedSlugs: ['mini-excavator', 'skid-steer', 'post-hole-digger'],
	},

	// Lifts & Access
	{
		slug: 'scissor-lift',
		name: 'Scissor Lift',
		categorySlug: 'lifts-access',
		description:
			'Electric and rough-terrain scissor lifts for elevated work platforms.',
		keywords: [
			'scissor lift rental',
			'scissor lift rental dallas',
			'scissor lift rental cost per day',
		],
		dbSubcategorySlugs: ['scissor-lift-rental'],
		relatedSlugs: ['boom-lift', 'scaffolding'],
	},
	{
		slug: 'boom-lift',
		name: 'Boom Lift',
		categorySlug: 'lifts-access',
		description:
			'Articulating and telescopic boom lifts for high-reach access.',
		keywords: [
			'boom lift rental',
			'boom lift rental dfw',
			'boom lift rental cost per day',
		],
		dbSubcategorySlugs: ['boom-lift-rental'],
		relatedSlugs: ['scissor-lift', 'telehandler'],
	},
	{
		slug: 'forklift',
		name: 'Forklift',
		categorySlug: 'lifts-access',
		description: 'Warehouse and rough-terrain forklifts for material handling.',
		keywords: [
			'forklift rental',
			'forklift rental dallas',
			'forklift rental cost',
		],
		dbSubcategorySlugs: ['forklift-rental'],
		relatedSlugs: ['telehandler', 'pallet-jack'],
	},
	{
		slug: 'telehandler',
		name: 'Telehandler',
		categorySlug: 'lifts-access',
		description:
			'Telescopic handlers for lifting and placing heavy loads at height.',
		keywords: ['telehandler rental', 'telehandler rental dfw'],
		dbSubcategorySlugs: ['telehandler-rental'],
		relatedSlugs: ['forklift', 'boom-lift'],
	},
	{
		slug: 'scaffolding',
		name: 'Scaffolding',
		categorySlug: 'lifts-access',
		description:
			'Frame and baker scaffolding sets for painting, siding, and masonry work.',
		keywords: ['scaffolding rental', 'scaffolding rental dallas'],
		dbSubcategorySlugs: ['scaffolding-rental'],
		relatedSlugs: ['scissor-lift', 'boom-lift'],
	},

	// Floor Care
	{
		slug: 'floor-sander',
		name: 'Floor Sander',
		categorySlug: 'floor-care',
		description: 'Drum and orbital sanders for refinishing hardwood floors.',
		keywords: ['floor sander rental', 'floor sander rental dallas'],
		dbSubcategorySlugs: ['floor-sander-rental'],
		relatedSlugs: ['floor-buffer', 'carpet-cleaner'],
	},
	{
		slug: 'carpet-cleaner',
		name: 'Carpet Cleaner',
		categorySlug: 'floor-care',
		description: 'Professional carpet extractors for deep cleaning.',
		keywords: ['carpet cleaner rental', 'carpet cleaner rental near me'],
		dbSubcategorySlugs: ['carpet-cleaner-rental'],
		relatedSlugs: ['floor-sander', 'pressure-washer'],
	},
	{
		slug: 'floor-buffer',
		name: 'Floor Buffer / Polisher',
		categorySlug: 'floor-care',
		description:
			'Rotary floor buffers and polishers for stripping, waxing, and polishing hard floors.',
		keywords: ['floor buffer rental', 'floor polisher rental'],
		dbSubcategorySlugs: ['floor-buffer-rental'],
		relatedSlugs: ['floor-sander', 'carpet-cleaner'],
	},

	// Power Tools
	{
		slug: 'pressure-washer',
		name: 'Pressure Washer',
		categorySlug: 'power-tools',
		description:
			'Gas and electric pressure washers for driveways, decks, and siding.',
		keywords: [
			'pressure washer rental',
			'pressure washer rental dallas',
			'pressure washer rental cost',
		],
		dbSubcategorySlugs: ['pressure-washer-rental'],
		relatedSlugs: ['paint-sprayer', 'carpet-cleaner'],
	},
	{
		slug: 'nail-gun',
		name: 'Nail Gun / Nailer',
		categorySlug: 'power-tools',
		description:
			'Framing, finish, and roofing nailers for construction projects.',
		keywords: ['nail gun rental', 'nailer rental dfw'],
		dbSubcategorySlugs: ['nail-gun-rental'],
		relatedSlugs: ['air-compressor', 'rotary-hammer'],
	},
	{
		slug: 'rotary-hammer',
		name: 'Rotary Hammer Drill',
		categorySlug: 'power-tools',
		description:
			'Heavy-duty rotary hammers for drilling into concrete and masonry.',
		keywords: ['rotary hammer rental', 'hammer drill rental'],
		dbSubcategorySlugs: ['rotary-hammer-rental'],
		relatedSlugs: ['jackhammer', 'concrete-saw'],
	},
	{
		slug: 'paint-sprayer',
		name: 'Paint Sprayer',
		categorySlug: 'power-tools',
		description: 'Airless paint sprayers for interior and exterior painting.',
		keywords: ['paint sprayer rental', 'paint sprayer rental dallas'],
		dbSubcategorySlugs: ['paint-sprayer-rental'],
		relatedSlugs: ['pressure-washer', 'floor-sander'],
	},
	{
		slug: 'power-drill',
		name: 'Power Drill',
		categorySlug: 'power-tools',
		description:
			'Cordless and corded drills for general construction and renovation.',
		keywords: ['power drill rental', 'drill rental near me'],
		dbSubcategorySlugs: ['power-drill-rental'],
		relatedSlugs: ['rotary-hammer', 'nail-gun'],
	},
	{
		slug: 'impact-wrench',
		name: 'Impact Wrench',
		categorySlug: 'power-tools',
		description: 'Pneumatic and cordless impact wrenches for heavy bolt work.',
		keywords: ['impact wrench rental', 'impact wrench rental near me'],
		dbSubcategorySlugs: ['impact-wrench-rental'],
		relatedSlugs: ['air-compressor', 'power-drill'],
	},

	// Generators & Power
	{
		slug: 'generator',
		name: 'Generator',
		categorySlug: 'generators-power',
		description:
			'Portable and towable generators from 2kW to 100kW+ for job sites and events.',
		keywords: [
			'generator rental',
			'generator rental dallas',
			'generator rental cost',
		],
		dbSubcategorySlugs: ['generator-rental'],
		relatedSlugs: ['air-compressor', 'welder'],
	},
	{
		slug: 'air-compressor',
		name: 'Air Compressor',
		categorySlug: 'generators-power',
		description: 'Towable and portable air compressors for pneumatic tools.',
		keywords: ['air compressor rental', 'air compressor rental dfw'],
		dbSubcategorySlugs: ['air-compressor-rental'],
		relatedSlugs: ['generator', 'nail-gun'],
	},
	{
		slug: 'welder',
		name: 'Welder',
		categorySlug: 'generators-power',
		description:
			'MIG, TIG, and stick welders for metal fabrication and repair.',
		keywords: ['welder rental', 'welder rental dallas'],
		dbSubcategorySlugs: ['welder-rental'],
		relatedSlugs: ['generator', 'air-compressor'],
	},

	// Material Handling
	{
		slug: 'forklift-warehouse',
		name: 'Warehouse Forklift',
		categorySlug: 'material-handling',
		description: 'Indoor electric forklifts for warehouse and dock operations.',
		keywords: [
			'warehouse forklift rental',
			'forklift rental dfw',
			'forklift rental cost',
		],
		dbSubcategorySlugs: ['forklift-rental'],
		relatedSlugs: ['pallet-jack', 'telehandler-material'],
	},
	{
		slug: 'telehandler-material',
		name: 'Telehandler',
		categorySlug: 'material-handling',
		description:
			'Telehandlers configured for palletized loads and material placement.',
		keywords: ['telehandler rental', 'reach forklift rental'],
		dbSubcategorySlugs: ['telehandler-rental'],
		relatedSlugs: ['forklift-warehouse', 'forklift'],
	},
	{
		slug: 'pallet-jack',
		name: 'Pallet Jack',
		categorySlug: 'material-handling',
		description:
			'Manual and electric pallet jacks for moving palletized loads.',
		keywords: ['pallet jack rental', 'pallet jack rental near me'],
		dbSubcategorySlugs: ['pallet-jack-rental'],
		relatedSlugs: ['forklift-warehouse', 'hand-truck'],
	},
	{
		slug: 'hand-truck',
		name: 'Hand Truck / Appliance Dolly',
		categorySlug: 'material-handling',
		description: 'Hand trucks and appliance dollies for moving heavy items.',
		keywords: ['appliance dolly rental', 'hand truck rental'],
		dbSubcategorySlugs: ['hand-truck-rental', 'appliance-dolly-rental'],
		relatedSlugs: ['pallet-jack', 'forklift-warehouse'],
	},
]

// ---------------------------------------------------------------------------
// Static "Typical DFW Rate" Prices
// ---------------------------------------------------------------------------

export const STATIC_VENDOR_PRICES: StaticVendorPrice[] = [
	// Lawn & Garden
	{
		equipmentSlug: 'aerator',
		dailyCents: 9500,
		weeklyCents: 28000,
		monthlyCents: null,
		depositCents: 15000,
		notes: 'Walk-behind core aerator',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'tiller',
		dailyCents: 8500,
		weeklyCents: 25000,
		monthlyCents: null,
		depositCents: 10000,
		notes: 'Rear-tine rototiller',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'chainsaw',
		dailyCents: 7000,
		weeklyCents: 21000,
		monthlyCents: null,
		depositCents: 10000,
		notes: '16-20 in. gas chainsaw',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'stump-grinder',
		dailyCents: 22000,
		weeklyCents: 70000,
		monthlyCents: null,
		depositCents: 25000,
		notes: 'Self-propelled, 25+ HP',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'wood-chipper',
		dailyCents: 25000,
		weeklyCents: 80000,
		monthlyCents: null,
		depositCents: 30000,
		notes: '6 in. capacity chipper',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'sod-cutter',
		dailyCents: 11000,
		weeklyCents: 35000,
		monthlyCents: null,
		depositCents: 15000,
		notes: '18 in. walk-behind',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'log-splitter',
		dailyCents: 14000,
		weeklyCents: 45000,
		monthlyCents: null,
		depositCents: 20000,
		notes: '25-ton hydraulic',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'post-hole-digger',
		dailyCents: 8000,
		weeklyCents: 24000,
		monthlyCents: null,
		depositCents: 10000,
		notes: 'One-man auger, 6-12 in.',
		isIllustrative: true,
	},

	{
		equipmentSlug: 'lawn-mower',
		dailyCents: 7500,
		weeklyCents: 22000,
		monthlyCents: null,
		depositCents: 10000,
		notes: '21 in. self-propelled',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'hedge-trimmer',
		dailyCents: 5000,
		weeklyCents: 16000,
		monthlyCents: null,
		depositCents: 5000,
		notes: '24 in. gas hedge trimmer',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'brush-cutter',
		dailyCents: 7500,
		weeklyCents: 22000,
		monthlyCents: null,
		depositCents: 10000,
		notes: 'Walk-behind brush cutter',
		isIllustrative: true,
	},

	// Concrete & Masonry
	{
		equipmentSlug: 'concrete-saw',
		dailyCents: 15000,
		weeklyCents: 50000,
		monthlyCents: null,
		depositCents: 20000,
		notes: '14 in. walk-behind',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'plate-compactor',
		dailyCents: 9500,
		weeklyCents: 30000,
		monthlyCents: null,
		depositCents: 15000,
		notes: 'Reversible plate compactor',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'tile-saw',
		dailyCents: 7000,
		weeklyCents: 22000,
		monthlyCents: null,
		depositCents: 10000,
		notes: '10 in. wet tile saw',
		isIllustrative: true,
	},

	{
		equipmentSlug: 'concrete-mixer',
		dailyCents: 7500,
		weeklyCents: 24000,
		monthlyCents: null,
		depositCents: 10000,
		notes: '6-9 cu ft portable',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'concrete-vibrator',
		dailyCents: 5000,
		weeklyCents: 16000,
		monthlyCents: null,
		depositCents: 5000,
		notes: 'Internal vibrator, 1-2 in. head',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'mortar-mixer',
		dailyCents: 7500,
		weeklyCents: 22000,
		monthlyCents: null,
		depositCents: 10000,
		notes: '6 cu ft paddle mixer',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'rebar-cutter',
		dailyCents: 6000,
		weeklyCents: 18000,
		monthlyCents: null,
		depositCents: 8000,
		notes: 'Electric, up to #6 rebar',
		isIllustrative: true,
	},

	// Demolition & Excavation
	{
		equipmentSlug: 'mini-excavator',
		dailyCents: 35000,
		weeklyCents: 120000,
		monthlyCents: 320000,
		depositCents: 50000,
		notes: '1-3 ton class',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'jackhammer',
		dailyCents: 10000,
		weeklyCents: 32000,
		monthlyCents: null,
		depositCents: 15000,
		notes: '35-65 lb electric',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'skid-steer',
		dailyCents: 32500,
		weeklyCents: 110000,
		monthlyCents: 300000,
		depositCents: 50000,
		notes: 'Standard bucket',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'trencher',
		dailyCents: 18000,
		weeklyCents: 55000,
		monthlyCents: 160000,
		depositCents: 25000,
		notes: '24-36 in. walk-behind',
		isIllustrative: true,
	},

	// Lifts & Access
	{
		equipmentSlug: 'scissor-lift',
		dailyCents: 22000,
		weeklyCents: 65000,
		monthlyCents: 180000,
		depositCents: 30000,
		notes: '19-26 ft platform height',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'boom-lift',
		dailyCents: 35000,
		weeklyCents: 110000,
		monthlyCents: 300000,
		depositCents: 50000,
		notes: '40-60 ft articulating',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'forklift',
		dailyCents: 22000,
		weeklyCents: 70000,
		monthlyCents: 200000,
		depositCents: 30000,
		notes: '5,000 lb capacity',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'telehandler',
		dailyCents: 37500,
		weeklyCents: 120000,
		monthlyCents: 340000,
		depositCents: 50000,
		notes: '6,000-8,000 lb capacity',
		isIllustrative: true,
	},

	{
		equipmentSlug: 'scaffolding',
		dailyCents: 5000,
		weeklyCents: 15000,
		monthlyCents: 40000,
		depositCents: 15000,
		notes: 'Per section, 5 ft x 5 ft',
		isIllustrative: true,
	},

	// Floor Care
	{
		equipmentSlug: 'floor-sander',
		dailyCents: 7500,
		weeklyCents: 25000,
		monthlyCents: null,
		depositCents: 10000,
		notes: 'Drum sander + edger combo',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'carpet-cleaner',
		dailyCents: 5000,
		weeklyCents: 15000,
		monthlyCents: null,
		depositCents: 5000,
		notes: 'Professional extractor',
		isIllustrative: true,
	},

	{
		equipmentSlug: 'floor-buffer',
		dailyCents: 5500,
		weeklyCents: 18000,
		monthlyCents: null,
		depositCents: 8000,
		notes: '17-20 in. rotary buffer',
		isIllustrative: true,
	},

	// Power Tools
	{
		equipmentSlug: 'pressure-washer',
		dailyCents: 8500,
		weeklyCents: 27000,
		monthlyCents: null,
		depositCents: 10000,
		notes: '2,500-3,500 PSI gas',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'nail-gun',
		dailyCents: 5000,
		weeklyCents: 16000,
		monthlyCents: null,
		depositCents: 5000,
		notes: 'Framing nailer + compressor',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'rotary-hammer',
		dailyCents: 6000,
		weeklyCents: 18000,
		monthlyCents: null,
		depositCents: 8000,
		notes: '1-1/8 in. SDS-plus',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'paint-sprayer',
		dailyCents: 7500,
		weeklyCents: 22000,
		monthlyCents: null,
		depositCents: 10000,
		notes: 'Airless, up to 3,000 PSI',
		isIllustrative: true,
	},

	{
		equipmentSlug: 'power-drill',
		dailyCents: 4000,
		weeklyCents: 12000,
		monthlyCents: null,
		depositCents: 5000,
		notes: 'Cordless 18V drill/driver kit',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'impact-wrench',
		dailyCents: 5000,
		weeklyCents: 16000,
		monthlyCents: null,
		depositCents: 5000,
		notes: '1/2 in. pneumatic',
		isIllustrative: true,
	},

	// Generators & Power
	{
		equipmentSlug: 'generator',
		dailyCents: 15000,
		weeklyCents: 45000,
		monthlyCents: 120000,
		depositCents: 25000,
		notes: '5-10 kW portable',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'air-compressor',
		dailyCents: 15000,
		weeklyCents: 45000,
		monthlyCents: 120000,
		depositCents: 20000,
		notes: '185 CFM towable',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'welder',
		dailyCents: 12000,
		weeklyCents: 35000,
		monthlyCents: 100000,
		depositCents: 20000,
		notes: 'MIG 250A',
		isIllustrative: true,
	},

	// Material Handling
	{
		equipmentSlug: 'forklift-warehouse',
		dailyCents: 18000,
		weeklyCents: 55000,
		monthlyCents: 160000,
		depositCents: 25000,
		notes: '5,000 lb indoor electric',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'telehandler-material',
		dailyCents: 37500,
		weeklyCents: 120000,
		monthlyCents: 340000,
		depositCents: 50000,
		notes: '6,000 lb reach forklift',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'pallet-jack',
		dailyCents: 5000,
		weeklyCents: 15000,
		monthlyCents: 40000,
		depositCents: 5000,
		notes: 'Manual, 5,500 lb capacity',
		isIllustrative: true,
	},
	{
		equipmentSlug: 'hand-truck',
		dailyCents: 2500,
		weeklyCents: 8000,
		monthlyCents: null,
		depositCents: 2500,
		notes: 'Appliance dolly, 800 lb capacity',
		isIllustrative: true,
	},
]

// ---------------------------------------------------------------------------
// Project Guides (4)
// ---------------------------------------------------------------------------

export const PROJECT_GUIDES: ProjectGuide[] = [
	{
		slug: 'tree-stump-removal',
		title: 'Tree Stump Removal',
		icon: 'trees',
		description:
			'Everything you need to grind stumps and clean up the debris after taking down a tree in your DFW yard.',
		equipmentSlugs: ['stump-grinder', 'chainsaw', 'wood-chipper'],
		tips: [
			'Call 811 at least 48 hours before digging to mark underground utilities.',
			'Stump grinders kick up rocks — wear safety glasses and clear the area.',
			'Rent a wood chipper to turn branches into free mulch for your garden beds.',
			'Need to haul away debris? Check our dumpster size calculator.',
		],
	},
	{
		slug: 'patio-walkway-prep',
		title: 'Patio & Walkway Prep',
		icon: 'layers',
		description:
			'Prepare a solid base for pavers, flagstone, or stamped concrete with the right compaction and cutting tools.',
		equipmentSlugs: [
			'plate-compactor',
			'concrete-saw',
			'sod-cutter',
			'mini-excavator',
		],
		tips: [
			'Strip existing sod with a sod cutter before excavating — it saves time.',
			'Compact your gravel base in 2-inch lifts for the best results.',
			'A mini excavator speeds up large patio digs but check your gate width first.',
			'Rent a concrete saw to make clean expansion-joint cuts after the pour.',
		],
	},
	{
		slug: 'hardwood-floor-refinish',
		title: 'Hardwood Floor Refinishing',
		icon: 'home',
		description:
			'Sand, stain, and seal your hardwood floors like a pro with the right rental equipment.',
		equipmentSlugs: ['floor-sander', 'carpet-cleaner', 'paint-sprayer'],
		tips: [
			'Rent a drum sander AND an edge sander — you need both for a flat finish.',
			'Start with 36-grit and work up to 100-grit for smooth, scratch-free results.',
			'Seal the room and tape off vents — sanding creates a lot of fine dust.',
			'Apply polyurethane with a paint sprayer for an even, professional coat.',
		],
	},
	{
		slug: 'spring-yard-cleanup',
		title: 'Spring Yard Cleanup',
		icon: 'trees',
		description:
			'Aerate, till, and tidy up your DFW yard after winter with rental equipment that saves hours of manual labor.',
		equipmentSlugs: [
			'aerator',
			'tiller',
			'pressure-washer',
			'post-hole-digger',
		],
		tips: [
			'Aerate when the soil is moist (not soaked) for deeper core penetration.',
			'Use a tiller to mix compost into garden beds before spring planting.',
			'Pressure wash driveways and patios to remove winter grime.',
			'A post hole digger makes quick work of new fence posts or mailbox installs.',
		],
	},
]

// ---------------------------------------------------------------------------
// Merge Logic — combines static prices with real DB listings
// ---------------------------------------------------------------------------

/** Shape of a DB listing passed from the loader. */
export interface DbListing {
	id: string
	title: string
	slug: string
	hourlyRateCents: number | null
	dailyRateCents: number | null
	weeklyRateCents: number | null
	monthlyRateCents: number | null
	depositCents: number | null
	attributes: unknown
	updatedAt: string | Date
	subcategories: Array<{ slug: string; name: string }>
	cities: Array<{ id: string; name: string; slug: string }>
	ownerBusiness: {
		name: string
		slug: string
		phone: string | null
		website: string | null
	} | null
}

/**
 * Build a full merged price map: equipment slug → MergedVendorPrice[].
 * DB vendor rows appear first; the static illustrative row is always appended.
 */
export function mergeWithDbListings(
	dbListings: DbListing[],
): Map<string, MergedVendorPrice[]> {
	const result = new Map<string, MergedVendorPrice[]>()

	// Index static prices by equipment slug
	const staticBySlug = new Map<string, StaticVendorPrice>()
	for (const sp of STATIC_VENDOR_PRICES) {
		staticBySlug.set(sp.equipmentSlug, sp)
	}

	for (const eqType of EQUIPMENT_TYPES) {
		const rows: MergedVendorPrice[] = []

		// Find DB listings whose subcategory slugs overlap with this equipment type
		for (const listing of dbListings) {
			const hasMatch = listing.subcategories.some((sc) =>
				eqType.dbSubcategorySlugs.includes(sc.slug),
			)
			if (!hasMatch) continue

			rows.push({
				vendorName: listing.ownerBusiness?.name ?? listing.title,
				vendorSlug: listing.ownerBusiness?.slug ?? null,
				dailyCents: listing.dailyRateCents,

				weeklyCents: listing.weeklyRateCents,
				monthlyCents: listing.monthlyRateCents,
				depositCents: listing.depositCents,
				notes: listing.title,
				isIllustrative: false,
			})
		}

		// Always append static illustrative row
		const staticRow = staticBySlug.get(eqType.slug)
		if (staticRow) {
			rows.push({
				vendorName: 'Typical DFW Rate',
				vendorSlug: null,
				dailyCents: staticRow.dailyCents,
				weeklyCents: staticRow.weeklyCents,
				monthlyCents: staticRow.monthlyCents,
				depositCents: staticRow.depositCents,
				notes: staticRow.notes,
				isIllustrative: true,
			})
		}

		result.set(eqType.slug, rows)
	}

	return result
}
